import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminNavbar from '../../component/adminNavbar';
import {
	Box,
	Container,
	Heading,
	Text,
	Flex,
	Card,
	CardBody,
	Badge,
	Spinner,
	Center,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Button,
	HStack,
	Icon,
} from '@chakra-ui/react';
import { MdHistory, MdChevronLeft, MdChevronRight } from 'react-icons/md';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const PAGE_SIZE = 50;

const ACTION_COLORS: Record<string, string> = {
	UPDATE_ORDER_STATUS: 'cyan',
	CREATE_PRODUCT: 'green',
	UPDATE_PRODUCT: 'blue',
	CREATE_USER: 'purple',
	UPDATE_USER: 'orange',
	RESET_PASSWORD: 'red',
};

const ACTION_LABELS: Record<string, string> = {
	UPDATE_ORDER_STATUS: 'Order Status',
	CREATE_PRODUCT: 'Create Product',
	UPDATE_PRODUCT: 'Edit Product',
	CREATE_USER: 'Create User',
	UPDATE_USER: 'Edit User',
	RESET_PASSWORD: 'Reset Password',
};

const AuditLog = () => {
	const [logs, setLogs] = useState<any[]>([]);
	const [count, setCount] = useState(0);
	const [offset, setOffset] = useState(0);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchLogs = async () => {
			setLoading(true);
			try {
				const token = localStorage.getItem('x-auth-token');
				const res = await axios.get(`${BASE_URL}/api/auditlog?offset=${offset}`, {
					headers: { 'x-auth-token': token },
				});
				setLogs(res.data.data || []);
				setCount(res.data.count || 0);
			} catch (e) {
				console.error('Failed to load audit log', e);
			} finally {
				setLoading(false);
			}
		};
		fetchLogs();
	}, [offset]);

	const totalPages = Math.ceil(count / PAGE_SIZE);
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

	return (
		<>
			<AdminNavbar />
			<Box ml={{ base: 0, md: '250px' }} bg="gray.50" minH="100vh">
				<Container maxW="container.xl" py={8} px={{ base: 4, md: 8 }}>
					{/* Header */}
					<Flex align="center" gap={3} mb={6}>
						<Flex w="44px" h="44px" borderRadius="xl" bg="purple.50" align="center" justify="center">
							<Icon as={MdHistory} boxSize={6} color="purple.500" />
						</Flex>
						<Box>
							<Heading size="lg" color="gray.800">Audit Log</Heading>
							<Text fontSize="sm" color="gray.400">{count} total events</Text>
						</Box>
					</Flex>

					<Card borderRadius="2xl" shadow="sm" overflow="hidden">
						{loading ? (
							<Center py={16}>
								<Spinner size="xl" thickness="4px" color="purple.500" />
							</Center>
						) : logs.length === 0 ? (
							<Center py={16}>
								<Box textAlign="center">
									<Icon as={MdHistory} boxSize={10} color="gray.300" mb={3} />
									<Text color="gray.400">No audit events yet.</Text>
								</Box>
							</Center>
						) : (
							<Box overflowX="auto">
								<Table variant="simple" size="sm">
									<Thead>
										<Tr bg="gray.50">
											<Th fontSize="xs" color="gray.500">Time</Th>
											<Th fontSize="xs" color="gray.500">Admin</Th>
											<Th fontSize="xs" color="gray.500">Action</Th>
											<Th fontSize="xs" color="gray.500">Target</Th>
											<Th fontSize="xs" color="gray.500">Details</Th>
										</Tr>
									</Thead>
									<Tbody>
										{logs.map((log) => (
											<Tr key={log.id} _hover={{ bg: 'gray.50' }}>
												<Td whiteSpace="nowrap">
													<Text fontSize="xs" color="gray.500">
														{new Date(log.created_at).toLocaleString('en-US', {
															month: 'short', day: 'numeric', year: 'numeric',
															hour: '2-digit', minute: '2-digit',
														})}
													</Text>
												</Td>
												<Td>
													<Text fontSize="sm" fontWeight="medium" color="gray.700">{log.admin_name || '—'}</Text>
													<Text fontSize="xs" color="gray.400">{log.admin_email}</Text>
												</Td>
												<Td>
													<Badge
														colorScheme={ACTION_COLORS[log.action] || 'gray'}
														variant="subtle"
														borderRadius="full"
														px={2}
														py={0.5}
														fontSize="xs"
													>
														{ACTION_LABELS[log.action] || log.action}
													</Badge>
												</Td>
												<Td>
													<Text fontSize="xs" color="gray.500" textTransform="capitalize">
														{log.target_type} #{log.target_id}
													</Text>
												</Td>
												<Td maxW="240px">
													<Text fontSize="xs" color="gray.600" noOfLines={2}>{log.details || '—'}</Text>
												</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							</Box>
						)}

						{/* Pagination */}
						{totalPages > 1 && (
							<Flex justify="space-between" align="center" px={4} py={3} borderTop="1px solid" borderColor="gray.100">
								<Text fontSize="sm" color="gray.500">
									Page {currentPage} of {totalPages}
								</Text>
								<HStack spacing={2}>
									<Button
										size="sm"
										variant="ghost"
										leftIcon={<Icon as={MdChevronLeft} />}
										isDisabled={offset === 0}
										onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
									>
										Prev
									</Button>
									<Button
										size="sm"
										variant="ghost"
										rightIcon={<Icon as={MdChevronRight} />}
										isDisabled={currentPage >= totalPages}
										onClick={() => setOffset(offset + PAGE_SIZE)}
									>
										Next
									</Button>
								</HStack>
							</Flex>
						)}
					</Card>
				</Container>
			</Box>
		</>
	);
};

export default AuditLog;
